import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  User, Camera, Mail, Phone, MapPin, Shield, Bell, Globe,
  Palette, Save, Upload, Loader2, ChevronRight, Trash2, Download, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserSettings() {
  const { user, refreshUser } = useAuth();
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [orgLogo, setOrgLogo] = useState(null);
  const [orgData, setOrgData] = useState(null);

  const [profile, setProfile] = useState({
    name: '', phone: '', address: '', emergency_contact: ''
  });

  const [settings, setSettings] = useState({
    notifications_email: true,
    notifications_push: true,
    notifications_sms: false,
    language: 'en',
    theme: 'dark'
  });

  const [consent, setConsent] = useState({
    gdpr_consent: false, gdpr_consent_date: null,
    hipaa_acknowledgment: false, hipaa_acknowledgment_date: null
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || '',
        phone: user.phone || '',
        address: user.address || '',
        emergency_contact: user.emergency_contact || ''
      });
      setPhotoPreview(user.profile_photo_url || null);
    }
    fetchSettings();
    fetchOrgData();
    fetchConsent();
  }, [user]);

  const fetchConsent = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/consent-status`, { withCredentials: true });
      setConsent(data);
    } catch (err) { console.error(err); }
  };

  const updateConsent = async (updates) => {
    try {
      await axios.put(`${API_URL}/api/users/gdpr-consent`, updates, { withCredentials: true });
      toast.success('Consent updated');
      fetchConsent();
    } catch (err) { toast.error('Failed'); }
  };

  const exportData = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/users/data-export`, { withCredentials: true });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fendanacura-data-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch (err) { toast.error('Export failed'); }
    setExporting(false);
  };

  const fetchSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/users/settings`, { withCredentials: true });
      setSettings(s => ({ ...s, ...data }));
    } catch (err) { console.error(err); }
  };

  const fetchOrgData = async () => {
    try {
      const [orgRes, logoRes] = await Promise.all([
        axios.get(`${API_URL}/api/org/me`, { withCredentials: true }).catch(() => null),
        axios.get(`${API_URL}/api/org/logo`, { withCredentials: true }).catch(() => null)
      ]);
      if (orgRes?.data) setOrgData(orgRes.data);
      if (logoRes?.data?.logo_url) setOrgLogo(logoRes.data.logo_url);
    } catch (err) { console.error(err); }
  };

  const saveProfile = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/users/profile`, profile, { withCredentials: true });
      if (typeof refreshUser === 'function') await refreshUser();
      toast.success('Profile updated');
    } catch (err) { toast.error('Failed to update profile'); }
    setLoading(false);
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/users/settings`, settings, { withCredentials: true });
      toast.success('Settings saved');
    } catch (err) { toast.error('Failed to save settings'); }
    setLoading(false);
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('File too large (max 5MB)');
    const form = new FormData();
    form.append('photo', file);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/users/profile-photo`, form, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoPreview(data.profile_photo_url);
      if (typeof refreshUser === 'function') await refreshUser();
      toast.success('Photo uploaded');
    } catch (err) { toast.error('Upload failed'); }
    setLoading(false);
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error('File too large (max 5MB)');
    const form = new FormData();
    form.append('logo', file);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/org/logo`, form, { withCredentials: true });
      setOrgLogo(data.logo_url);
      toast.success('Company logo uploaded');
    } catch (err) { toast.error(err.response?.data?.detail || 'Logo upload failed'); }
    setLoading(false);
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'organization', label: 'Organization', icon: Shield },
    { id: 'compliance', label: 'Privacy & Compliance', icon: FileText },
    { id: 'account', label: 'Account', icon: Globe },
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-settings-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground mt-1">Manage your profile and preferences</p>
        </div>

        <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
          {tabs.map(t => (
            <Button key={t.id} variant="ghost" size="sm"
              className={`whitespace-nowrap ${activeTab === t.id ? 'text-[#00A6A0] border-b-2 border-[#00A6A0] rounded-none' : 'text-muted-foreground'}`}
              onClick={() => setActiveTab(t.id)} data-testid={`settings-tab-${t.id}`}>
              <t.icon className="w-4 h-4 mr-1.5" />{t.label}
            </Button>
          ))}
        </div>

        {/* PROFILE TAB */}
        {activeTab === 'profile' && (
          <div className="space-y-6">
            <Card className="glass-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-6">
                  <div className="relative">
                    <Avatar className="h-20 w-20">
                      {photoPreview ? (
                        <AvatarImage src={photoPreview} alt={user?.name} />
                      ) : (
                        <AvatarFallback className="bg-[#00A6A0]/20 text-[#00A6A0] text-2xl font-medium">
                          {getInitials(user?.name)}
                        </AvatarFallback>
                      )}
                    </Avatar>
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-[#00A6A0] text-[#0F1419] hover:bg-[#008A85]"
                      data-testid="upload-photo-btn"
                    >
                      <Camera className="w-3.5 h-3.5" />
                    </button>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                  </div>
                  <div>
                    <h2 className="text-lg font-heading text-foreground">{user?.name}</h2>
                    <p className="text-sm text-muted-foreground">{user?.email}</p>
                    <div className="flex gap-2 mt-1">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0]">{user?.role}</span>
                      {user?.staff_number && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FF6F61]/10 text-[#FF6F61]">{user.staff_number}</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground">Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Full Name</label>
                    <Input value={profile.name} onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                      className="bg-muted border-border text-foreground" data-testid="settings-name-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Phone</label>
                    <Input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                      className="bg-muted border-border text-foreground" data-testid="settings-phone-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Address</label>
                    <Input value={profile.address} onChange={e => setProfile(p => ({ ...p, address: e.target.value }))}
                      className="bg-muted border-border text-foreground" data-testid="settings-address-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground block mb-1">Emergency Contact</label>
                    <Input value={profile.emergency_contact} onChange={e => setProfile(p => ({ ...p, emergency_contact: e.target.value }))}
                      className="bg-muted border-border text-foreground" data-testid="settings-emergency-input" />
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <p className="text-xs text-muted-foreground">USIC: <span className="text-[#00A6A0] font-mono">{user?.usic}</span></p>
                  {user?.staff_number && <p className="text-xs text-muted-foreground">Staff #: <span className="text-[#FF6F61] font-mono">{user.staff_number}</span></p>}
                </div>
                <Button className="btn-primary" onClick={saveProfile} disabled={loading} data-testid="save-profile-btn">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Profile
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* NOTIFICATIONS TAB */}
        {activeTab === 'notifications' && (
          <Card className="glass-card border-border">
            <CardHeader>
              <CardTitle className="text-base text-foreground flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#00A6A0]" />Notification Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: 'notifications_email', label: 'Email Notifications', desc: 'Receive shift updates, invitations, and alerts via email' },
                { key: 'notifications_push', label: 'Push Notifications', desc: 'Browser push notifications for real-time updates' },
                { key: 'notifications_sms', label: 'SMS Notifications', desc: 'Text message alerts for urgent notifications (requires Twilio)' }
              ].map(n => (
                <div key={n.key} className="flex items-center justify-between p-4 bg-muted rounded-xl border border-border">
                  <div>
                    <p className="text-sm text-foreground font-medium">{n.label}</p>
                    <p className="text-xs text-muted-foreground">{n.desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-testid={`toggle-${n.key}`}>
                    <input type="checkbox" checked={settings[n.key]} onChange={e => setSettings(s => ({ ...s, [n.key]: e.target.checked }))}
                      className="sr-only peer" />
                    <div className="w-10 h-5 bg-muted peer-checked:bg-[#00A6A0] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                  </label>
                </div>
              ))}
              <Button className="btn-primary" onClick={saveSettings} disabled={loading} data-testid="save-notifications-btn">
                <Save className="w-4 h-4 mr-2" />Save Preferences
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ORGANIZATION TAB */}
        {activeTab === 'organization' && (
          <div className="space-y-6">
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#00A6A0]" />Company Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {orgData ? (
                  <>
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        {orgLogo ? (
                          <img src={orgLogo} alt="Logo" className="w-16 h-16 rounded-xl object-cover border border-border" />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-muted border border-border flex items-center justify-center">
                            <Shield className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        {['agency_admin', 'ceo'].includes(user?.role) && (
                          <>
                            <button
                              onClick={() => logoRef.current?.click()}
                              className="absolute -bottom-1 -right-1 p-1.5 rounded-full bg-[#FF6F61] text-[#0F1419] hover:bg-[#D27D5B]"
                              data-testid="upload-logo-btn"
                            >
                              <Camera className="w-3 h-3" />
                            </button>
                            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
                          </>
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-heading text-foreground">{orgData.name}</h3>
                        <p className="text-xs text-muted-foreground">CISC: <span className="text-[#FF6F61] font-mono">{orgData.cisc}</span></p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3 pt-2">
                      <div className="p-3 bg-muted rounded-lg border border-border">
                        <p className="text-[10px] text-muted-foreground uppercase">Address</p>
                        <p className="text-sm text-foreground">{orgData.address || 'Not set'}</p>
                      </div>
                      <div className="p-3 bg-muted rounded-lg border border-border">
                        <p className="text-[10px] text-muted-foreground uppercase">Phone</p>
                        <p className="text-sm text-foreground">{orgData.phone || 'Not set'}</p>
                      </div>
                      {['ceo', 'agency_admin'].includes(user?.role) && (
                        <>
                          <div className="p-3 bg-muted rounded-lg border border-border" data-testid="org-subscription-tier">
                            <p className="text-[10px] text-muted-foreground uppercase">Subscription</p>
                            <p className="text-sm text-[#00A6A0]">{orgData.subscription_tier || 'Pending'}</p>
                          </div>
                          <div className="p-3 bg-muted rounded-lg border border-border" data-testid="org-active-users">
                            <p className="text-[10px] text-muted-foreground uppercase">Active Users</p>
                            <p className="text-sm text-foreground">{orgData.active_users || 0} / {orgData.expected_users || 0}</p>
                          </div>
                          <div className="p-3 bg-muted rounded-lg border border-border col-span-2" data-testid="org-region">
                            <p className="text-[10px] text-muted-foreground uppercase mb-1">Compliance Region</p>
                            <div className="flex items-center gap-2">
                              <select
                                value={orgData.region || 'UK'}
                                onChange={async (e) => {
                                  const newRegion = e.target.value;
                                  try {
                                    await axios.put(`${API_URL}/api/org/region`, { region: newRegion }, { withCredentials: true });
                                    setOrgData(prev => ({ ...prev, region: newRegion }));
                                    toast.success(`Region updated to ${newRegion}`);
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
                                }}
                                className="flex-1 bg-background border border-border rounded-md px-3 py-1.5 text-foreground text-sm"
                                data-testid="region-selector"
                              >
                                <option value="UK">United Kingdom (CQC + NHS)</option>
                                <option value="IE">Republic of Ireland (HIQA + HSE)</option>
                                <option value="US">United States (HIPAA + CMS)</option>
                                <option value="CA">Canada (PHIPA + Accreditation Canada)</option>
                                <option value="AU">Australia (ACQSC + NDIS)</option>
                                <option value="NZ">New Zealand (HDSS)</option>
                                <option value="EU">European Union (EU GDPR)</option>
                              </select>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-1">Determines compliance frameworks, training, and document templates</p>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-muted-foreground text-center py-6">No organization associated with your account</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* COMPLIANCE TAB */}
        {activeTab === 'compliance' && (
          <div className="space-y-6">
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#00A6A0]" />GDPR Compliance
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted rounded-xl border border-border">
                  <div>
                    <p className="text-sm text-foreground font-medium">Data Processing Consent</p>
                    <p className="text-xs text-muted-foreground">I consent to FendanaCura processing my personal data as described in the Privacy Policy</p>
                    {consent.gdpr_consent_date && (
                      <p className="text-[10px] text-muted-foreground mt-1">Consented: {new Date(consent.gdpr_consent_date).toLocaleDateString()}</p>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-testid="gdpr-consent-toggle">
                    <input type="checkbox" checked={consent.gdpr_consent}
                      onChange={e => updateConsent({ consent: e.target.checked, hipaa_acknowledgment: consent.hipaa_acknowledgment })}
                      className="sr-only peer" />
                    <div className="w-10 h-5 bg-muted peer-checked:bg-[#00A6A0] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                  </label>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#FF6F61]" />HIPAA Acknowledgment
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  FendanaCura handles Protected Health Information (PHI) as part of care management. By enabling this, you acknowledge that you understand and agree to comply with HIPAA regulations when using this platform.
                </p>
                <div className="flex items-center justify-between p-4 bg-muted rounded-xl border border-border">
                  <div>
                    <p className="text-sm text-foreground font-medium">HIPAA Acknowledgment</p>
                    <p className="text-xs text-muted-foreground">I acknowledge HIPAA requirements for handling PHI</p>
                    {consent.hipaa_acknowledgment_date && (
                      <p className="text-[10px] text-muted-foreground mt-1">Acknowledged: {new Date(consent.hipaa_acknowledgment_date).toLocaleDateString()}</p>
                    )}
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-testid="hipaa-toggle">
                    <input type="checkbox" checked={consent.hipaa_acknowledgment}
                      onChange={e => updateConsent({ consent: consent.gdpr_consent, hipaa_acknowledgment: e.target.checked })}
                      className="sr-only peer" />
                    <div className="w-10 h-5 bg-muted peer-checked:bg-[#FF6F61] rounded-full transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:w-4 after:h-4 after:bg-white after:rounded-full after:transition-transform peer-checked:after:translate-x-5" />
                  </label>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#A8E6CF]" />Data Portability (GDPR Article 20)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-muted-foreground">Download all personal data held by FendanaCura in a machine-readable format (JSON).</p>
                <Button variant="outline" className="border-[#00A6A0]/30 text-[#00A6A0]" onClick={exportData} disabled={exporting} data-testid="export-data-btn">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                  {exporting ? 'Exporting...' : 'Export My Data'}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ACCOUNT TAB */}
        {activeTab === 'account' && (
          <div className="space-y-6">
            <Card className="glass-card border-border">
              <CardHeader>
                <CardTitle className="text-base text-foreground">Account Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-3 bg-muted rounded-lg border border-border flex justify-between items-center">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Email</p>
                    <p className="text-sm text-foreground">{user?.email}</p>
                  </div>
                  <Mail className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="p-3 bg-muted rounded-lg border border-border flex justify-between items-center">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Role</p>
                    <p className="text-sm text-[#00A6A0] capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                  <Shield className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="p-3 bg-muted rounded-lg border border-border flex justify-between items-center">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase">Member Since</p>
                    <p className="text-sm text-foreground">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '--'}</p>
                  </div>
                  <Globe className="w-4 h-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-red-500/20">
              <CardHeader>
                <CardTitle className="text-base text-red-400 flex items-center gap-2">
                  <Trash2 className="w-5 h-5" />Danger Zone
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground mb-3">Permanently delete your account and all associated data.</p>
                <Link to="/delete-data">
                  <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="go-delete-account-btn">
                    <Trash2 className="w-4 h-4 mr-2" />Delete Account
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <div className="flex gap-3 text-xs text-muted-foreground">
              <Link to="/privacy" className="hover:text-muted-foreground underline">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-muted-foreground underline">Terms of Service</Link>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
